import { type Option, type TaskId } from "@tento-lona/sheets";
import { UserOrderable } from "@tento-core/sort";

import { TaskPresentation } from "../task-presentation.ts";
import type { TasksClient } from "@tento-lona";
import { Network } from "@tento-lona/network";

/**
 * A task as a task row paints it: `@tento/lona`'s `TasksClient.Snapshot` (the
 * wire task with its `id`) plus the row's local `isPending` flag. `position` comes from
 * `Network.TaskAttributes` (`Option<Rational>`), so a `TaskRowItem` is a
 * `UserOrderableOpt` without extending it.
 */
export interface TaskRowItem extends TasksClient.Snapshot {
  /** Whether the task is an optimistic one the server has not answered yet. */
  isPending: boolean;
}

/**
 * Types and values that go with `TaskRowItem`: `SortBy`, `make`, `of`, `Draft`,
 * `fromSnapshot`, `comparator`.
 */
export namespace TaskRowItem {
  export type SortBy =
    | "user-defined"
    | "label"
    | "importance"
    | "due-date"
    | "status";

  export function make(): Network.TaskAttributes {
    return Network.TaskAttributes.make({ title: "", completedAt: null, createdAt: null, description: null, displayId: null, displayNumber: null, dueDate: null, eventId: null, importance: null, position: null, scheduledEnd: null, scheduledStart: null, status: null });
  }

  /**
   * A task item from its id and its wire attributes (every key, `null`
   * stated, like a generated `of`); `isPending` left out is `false` (a
   * settled task).
   */
  export function of(fields: Draft): TaskRowItem {
    const { isPending, ...task } = fields;
    return { ...Network.TaskAttributes.make(task), id: task.id, isPending: isPending ?? false };
  }

  /** What `of` takes: the wire attributes, the id, and whether the task is still pending. */
  export type Draft = Network.TaskAttributes & {
    id: TaskId;
    isPending?: boolean;
  };

  /** A settled task (`isPending: false`) as `TasksClient.list` answers it. */
  export function fromSnapshot(snapshot: TasksClient.Snapshot): TaskRowItem {
    return { ...snapshot, isPending: false };
  }

  export function comparator(
    sortBy: TaskRowItem.SortBy,
    ascending: number
  ): (a: TaskRowItem, b: TaskRowItem) => number {
    switch (sortBy) {
      case "label":
        return (a, b) =>
          ascending * (a.title ?? "").localeCompare(b.title ?? "");
      case "importance":
        return (a, b) => {
          if (!a.importance) return Number.MAX_SAFE_INTEGER;
          if (!b.importance) return -Number.MAX_SAFE_INTEGER;
          return (
            ascending *
            (TaskPresentation.importanceRank(a.importance) -
              TaskPresentation.importanceRank(b.importance))
          );
        };
      case "due-date":
        return (a, b) => {
          if (!a.dueDate) return Number.MAX_SAFE_INTEGER;
          if (!b.dueDate) return -Number.MAX_SAFE_INTEGER;
          return ascending * (a.dueDate.mse - b.dueDate.mse);
        };
      case "status":
        return (a, b) => {
          if (!a.status) return Number.MAX_SAFE_INTEGER;
          if (!b.status) return -Number.MAX_SAFE_INTEGER;
          return (
            ascending *
            (TaskPresentation.statusRank(a.status) -
              TaskPresentation.statusRank(b.status))
          );
        };
      case "user-defined":
        return UserOrderable.compareOptChain((a, b) =>
          a.id.localeCompare(b.id)
        );
    }
  }
}
